<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-row>
        <b-col cols="12" md="10">
          <b-card header-tag="header" footer-tag="footer">
            <template slot="header">
              <i class="fa fa-trophy"></i>
              <strong>Saved Ladders</strong>
              <span v-if="servers[activeServer].readonly" class="badge badge-danger">
                Read-only
              </span>
              <div class="card-actions">
                <div class="btn-group" role="group" aria-label="Basic example">
                  <template v-for="(item, index) in servers" v-if="!item.readonly">
                    <button
                      type="button"
                      @click="switchServer(index)"
                      :class="['btn-' + item.colour, activeServer === index ? 'active' : '']"
                      class="btn btn-small"
                    >
                      {{ capitalizeFirstLetter(index) }}
                    </button>
                  </template>
                </div>
              </div>
            </template>

            <div>
              <ag-grid-vue
                style="width: 100%; height: 700px;"
                class="ag-theme-balham-dark"
                :gridOptions="gridOptions"
                :enableColResize="true"
                :enableSorting="true"
                :rowData="ladders"
                :masterDetail="true"
                :suppressClickEdit="true"
                :enableFilter="true"
                :groupHeaders="true"
                :isRowMaster="isRowMaster"
                :detailCellRendererParams="detailCellRendererParams"
                :suppressRowClickSelection="true"
                :toolPanelSuppressGroups="true"
                :toolPanelSuppressValues="true"
              >
              </ag-grid-vue>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import Servers from '../../_servers'
import { mapActions, mapGetters } from 'vuex'
import { AgGridVue } from 'ag-grid-vue'
import 'ag-grid-enterprise'
import { DefaultGridOptions } from '@/default_table_settings'
import { applyGridPersistence } from '@/grid_persistence'
import moment from "moment/moment";

export default {
  components: { AgGridVue },

  data() {
    return {
      gridOptions: {},
      isRowMaster: null,
      activeServer: 'gold',
      detailCellRendererParams: null,
      servers: Servers,
      masterCols: [

        {
          field: 'date',
          headerName: 'Timestamp',
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: {
            innerRenderer: params => this.formatDateDisplay(params.value),
          },
          sortable: true,
          filter: 'agDateColumnFilter',
          tooltipValueGetter: params => {
            if (!params.value) return '';
            const m = moment(params.value);
            return `Local: ${m.local().format('YYYY-MM-DD HH:mm:ss')}`;
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight, cellValue) => {
              const cellDate = new Date(cellValue);
              // Strip time for comparison (if needed)
              const cellDateNoTime = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());

              if (cellDateNoTime < filterLocalDateAtMidnight) return -1;
              if (cellDateNoTime > filterLocalDateAtMidnight) return 1;
              return 0;
            },
          },
          sort: 'desc',
        },
        { field: 'type', headerName: 'Type' },
      ],
      detailCols: [
        { field: 'rank', headerName: '#' },
        { field: 'username', headerName: 'Username' },
        { field: 'guild', headerName: 'Guild' },
        { field: 'rating', headerName: 'Rating' },
        { field: 'wins', headerName: 'Wins' },
        { field: 'losses', headerName: 'Losses' }
      ]
    }
  },

  computed: {
    ...mapGetters([
      'ladders',
      // getter we just added in Vuex
      'ladderEntries'
    ])
  },

  beforeMount() {
    this.isRowMaster = dataItem => true

    this.detailCellRendererParams = {
      detailGridOptions: {
        columnDefs: this.detailCols,
        defaultColDef: { flex: 1 },
        getContextMenuItems: this.getContextMenuItems
      },

      getDetailRowData: params => {
        // 1) fire off the socket → Vuex action:
        this.emit('getSavedLaddersEntries', {data: params.data.id, server: this.activeServer})

        // 2) watch the Vuex getter until it updates,
        //    then hand the entries to AG-Grid:
        const unwatch = this.$store.watch(
          // watch the raw array for changes:
          state => this.$store.getters.ladderEntries,
          entries => {
            params.successCallback(entries)
            unwatch() // stop watching once we've populated the detail
          }
        )
      }
    }

    this.gridOptions = Object.assign({}, DefaultGridOptions, {
      suppressRowClickSelection: true,
      rowSelection: 'multiple'
    })
    applyGridPersistence(this.gridOptions, 'ladder-grid')

    this.gridOptions.rowData = this.ladders

    this.gridOptions.columnDefs = this.masterCols
  },

  mounted() {
  },

  created() {
    // initial load of the master list
    this.emit('getSavedLadders', this.activeServer)
  },

  methods: {
    ...mapActions([
      'getSavedLadders',
      'getSavedLaddersEntries'
    ]),
    truncateBotCell(value, maxLength) {
      const text = value == null || value === '' ? '-' : String(value)
      if (!maxLength || text.length <= maxLength) return text
      if (maxLength <= 3) return text.slice(0, maxLength)
      return text.slice(0, maxLength - 3) + '...'
    },
    buildBotRows(rawRows, textLimits) {
      return rawRows.map(r => {
        const row = [
          `${r.rank}.`,
          this.truncateBotCell(r.username, textLimits.username),
          this.truncateBotCell(r.guild || '-', textLimits.guild),
          String(r.rating),
          String(r.wins),
          String(r.losses)
        ]
        if (r.pr != null) {
          row.push(String(r.pr))
        }
        return row
      })
    },
    buildBotMarkdown(rawRows, textLimits) {
      const rows = this.buildBotRows(rawRows, textLimits)
      const headers = ['#', 'Username', 'Guild', 'Rating', 'Wins', 'Losses']
      if (rows.some(r => r.length > headers.length)) {
        headers.push('PR')
      }
      const tableRows = [headers, ...rows]
      const widths = headers.map((_, ci) =>
        Math.max(...tableRows.map(r => (r[ci] || '').length))
      )

      const lines = tableRows.map(r =>
        headers
          .map((_, ci) => (r[ci] || '').padEnd(widths[ci]))
          .join('  ')
          .trimEnd()
      )

      return '```md\n' + lines.join('\n') + '\n```'
    },
    exportDisplayedRows(api) {
      const rawRows = []
      api.forEachNodeAfterFilterAndSort(node => {
        const r = node.data
        rawRows.push(r)
      })

      if (rawRows.length === 0) {
        this.$bvToast.toast('No rows to export.', {
          title: 'Export Skipped',
          variant: 'warning',
          solid: true
        })
        return
      }

      const discordLimit = 2000
      const minRanks = Math.min(25, rawRows.length)
      const textLimits = { username: 24, guild: 24 }
      let md = this.buildBotMarkdown(rawRows.slice(0, minRanks), textLimits)

      while (md.length > discordLimit && (textLimits.username > 10 || textLimits.guild > 10)) {
        if (textLimits.guild >= textLimits.username && textLimits.guild > 10) {
          textLimits.guild -= 2
        } else if (textLimits.username > 10) {
          textLimits.username -= 2
        }
        md = this.buildBotMarkdown(rawRows.slice(0, minRanks), textLimits)
      }

      let exportedRanks = minRanks
      for (let count = minRanks + 1; count <= rawRows.length; count++) {
        const candidate = this.buildBotMarkdown(rawRows.slice(0, count), textLimits)
        if (candidate.length > discordLimit) {
          break
        }
        md = candidate
        exportedRanks = count
      }

      // copy and notify
      navigator.clipboard.writeText(md)
        .then(() =>
          this.$bvToast.toast(`Copied ${exportedRanks} ranks to clipboard (${md.length}/${discordLimit} characters).`, {
            title: 'Export Successful',
            variant: 'success',
            solid: true
          })
        )
        .catch(() => alert('Failed to copy — try again.'))
    },
    getContextMenuItems(params) {
      // grab the defaults, then append our own
      const defaultItems = params.defaultItems.slice()
      defaultItems.push({
        name: 'Export to Bot Table',
        action: () => this.exportDisplayedRows(params.api),
        icon: '<i class="icon-share"></i>'  // optional icon
      })
      return defaultItems
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1)
    },

    switchServer(index) {
      this.activeServer = index
      this.emit('getSavedLadders', index)
    },

    onGridSizeChanged() {
      if (this.gridOptions.api) {
        this.gridOptions.api.sizeColumnsToFit()
      }
    },
  }
}
</script>

<style>
/* … */
</style>
